import { useMutation } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import {
  CreateFilteredPointCloudJobParameters,
  DeleteFilteredPointCloudParameters,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { isValidNumber } from '../../../../utils/math/number';
import { isDefined } from '../../../../utils/variables';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { Select } from '../../select/select';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import {
  FilteredPointCloudClassesOptions,
  FilteredPointCloudFormatOptions,
} from '../new-cloud-process-dialog/add-ons';

function useDeleteMutation(props: { onError: (error: Error) => void; onSuccess: () => void }) {
  return useMutation({
    mutationFn: async (params: DeleteFilteredPointCloudParameters) =>
      SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().deleteFilteredPointCloud(params),
    retry: false,
    networkMode: 'always',
    onError(error) {
      props.onError(error);
    },
    onSuccess() {
      props.onSuccess();
    },
  });
}

function useSubmitMutation(props: { onError: (error: Error) => void; onSuccess: () => void }) {
  return useMutation({
    mutationFn: async (params: CreateFilteredPointCloudJobParameters) =>
      SkyMapAxiosServiceFactory.instance
        .createGeodataServiceV0()
        .createFilteredPointCloudJob(params),
    retry: false,
    networkMode: 'always',
    onError(error) {
      props.onError(error);
    },
    onSuccess() {
      props.onSuccess();
    },
  });
}

export const FilteredPointCloudDialog = (props: {
  dialog: ReturnType<typeof useDialog>;
  geodata: GeodataModel;
}) => {
  const { buildErrorList, handleError } = useErrorHandling();
  const { t } = useTranslation('cloudProcessing');
  const [classes, setClasses] = useState<FilteredPointCloudClassesOptions>('ALL');
  const [exportFormat, setExportFormat] = useState<FilteredPointCloudFormatOptions>('LAS');
  const [spacing, setSpacing] = useState('0.1');
  const hasFilteredPointCloud = isDefined(props.geodata.filteredPointCloud);

  const onSuccess = useMemo(() => {
    return () => {
      props.dialog.hide();
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: hasFilteredPointCloud
          ? t('geodataPage.filteredPointCloudDialog.successRemoved', { ns: 'cloudProcessing' })
          : t('geodataPage.filteredPointCloudDialog.successQueued', { ns: 'cloudProcessing' }),
      });
    };
  }, [props.dialog, hasFilteredPointCloud, t]);

  const onError = useMemo(() => {
    return (error: Error) => handleError(error);
  }, [handleError]);

  const deleteMutation = useDeleteMutation({ onSuccess, onError });
  const submitMutation = useSubmitMutation({ onSuccess, onError });

  const isPending = deleteMutation.isPending || submitMutation.isPending;
  const canCreateFilteredPointCloudJob =
    props.geodata.status === 'generate_pointcloud_finished' ||
    props.geodata.status === 'generate_skyview_data_finished';

  return (
    <Dialog closeIcon={true} closeOnDimmerClick={true} onClose={props.dialog.hide}>
      {{
        header: t('geodataPage.filteredPointCloudDialog.header', { ns: 'cloudProcessing' }),
        content: (
          <OverlayLoader visible={isPending}>
            <Stack spacing={1}>
              <InfoBox color="yellow">
                {hasFilteredPointCloud
                  ? t('geodataPage.filteredPointCloudDialog.descriptionExisting', {
                      ns: 'cloudProcessing',
                    })
                  : t('geodataPage.filteredPointCloudDialog.descriptionNew', {
                      ns: 'cloudProcessing',
                    })}
              </InfoBox>
              {!hasFilteredPointCloud && (
                <LabelledContainer
                  text={t('geodataPage.filteredPointCloudDialog.spacingLabel', {
                    ns: 'cloudProcessing',
                  })}
                >
                  <TextBox
                    step={0.001}
                    type="number"
                    value={spacing}
                    onChange={(e) => setSpacing(e.target.value)}
                  />
                </LabelledContainer>
              )}
              {!hasFilteredPointCloud && (
                <LabelledContainer
                  text={t('geodataPage.filteredPointCloudDialog.exportFormatLabel', {
                    ns: 'cloudProcessing',
                  })}
                >
                  <Select
                    options={
                      [{ name: 'LAS' }, { name: 'LAZ' }, { name: 'DXF' }] as {
                        name: FilteredPointCloudFormatOptions;
                      }[]
                    }
                    value={exportFormat}
                    onChange={(e) =>
                      setExportFormat(e.target.value as FilteredPointCloudFormatOptions)
                    }
                  />
                </LabelledContainer>
              )}
              {!hasFilteredPointCloud && (
                <LabelledContainer
                  text={t('geodataPage.filteredPointCloudDialog.pointClassesLabel', {
                    ns: 'cloudProcessing',
                  })}
                >
                  <Select
                    options={
                      [
                        {
                          id: 'ALL',
                          name: t('setupDialog.addOns.filteredPointCloud.classesAll', {
                            ns: 'cloudProcessing',
                          }),
                        },
                        {
                          id: 'GROUND',
                          name: t('setupDialog.addOns.filteredPointCloud.classesGround', {
                            ns: 'cloudProcessing',
                          }),
                        },
                      ] as { id: FilteredPointCloudClassesOptions; name: string }[]
                    }
                    value={classes}
                    onChange={(e) => setClasses(e.target.value as FilteredPointCloudClassesOptions)}
                  />
                </LabelledContainer>
              )}
              {buildErrorList()}
            </Stack>
          </OverlayLoader>
        ),
        footer: {
          left: (
            <>
              {hasFilteredPointCloud ? (
                <Button
                  color="error"
                  leftIcon={{ icon: ['fad', 'trash-alt'] }}
                  variant="contained"
                  onClick={() => deleteMutation.mutate({ path: { geodataId: props.geodata.id } })}
                >
                  {t('delete', { ns: 'common' })}
                </Button>
              ) : (
                <Button
                  color="primary"
                  disabled={!canCreateFilteredPointCloudJob || !isValidNumber(spacing)}
                  variant="contained"
                  onClick={() =>
                    submitMutation.mutate({
                      path: { geodataId: props.geodata.id },
                      body: {
                        spacing: Number(spacing),
                        classes,
                        format: exportFormat,
                      },
                    })
                  }
                >
                  {t('start', { ns: 'common' })}
                </Button>
              )}
            </>
          ),
        },
      }}
    </Dialog>
  );
};
